import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Select,
  Datepicker,
} from 'components';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import { globalPropTypes } from 'helpers';
import { statusOptions, titleOptions } from '../../CisOptions';
import { toApiDate } from 'helpers/date';

const EditCertificateHolder = ({
  state,
  dispatch,
  updateFieldCreator,
  errors,
  updateCertificateHolder,
  removeError,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const submit = () => {
    const body = {
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      title: state.title,
      middleName: state.middleName,
      birthDate: state.birthDate ? toApiDate(state.birthDate) : null,
      status: state.status,
    };

    updateCertificateHolder(state.id, body, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">
              {t('page.user.cis.certificateHolders.editCertificateHolder')}
            </Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="title"
                  label={t('page.user.cis.certificateHolders.title')}
                  error={errors.title}
                  value={titleOptions.find((option) => option.value === state.title) || ''}
                  clear={() => handleChangeSelect('title')(null)}
                >
                  <Select
                    id="title"
                    placeholder={t('page.user.cis.certificateHolders.titlePlaceholder')}
                    value={titleOptions.find((option) => option.value === state.title) || ''}
                    options={titleOptions}
                    onChange={handleChangeSelect('title', 'title')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="email"
                  label={t('page.user.cis.certificateHolders.email')}
                  error={errors.email}
                >
                  <input
                    id="email"
                    type="text"
                    placeholder={t('page.user.cis.certificateHolders.emailPlaceholder')}
                    value={state.email}
                    onChange={handleChangeNative('email', 'email')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="birthDate"
                  label={t('page.user.cis.certificateHolders.birthDate')}
                  value={state.birthDate}
                  clear={() => handleChangeCustom('birthDate')(null)}
                  error={errors.birthDate}
                >
                  <Datepicker
                    id="birthDate"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.certificateHolders.birthDatePlaceholder')}
                    selected={state.birthDate}
                    onChange={handleChangeCustom('birthDate', 'birthDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="firstName"
                  label={t('page.user.cis.certificateHolders.firstName')}
                  required
                  error={errors.firstName}
                >
                  <input
                    id="firstName"
                    type="text"
                    placeholder={t('page.user.cis.certificateHolders.firstNamePlaceholder')}
                    value={state.firstName}
                    onChange={handleChangeNative('firstName', 'firstName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="middleName"
                  label={t('page.user.cis.certificateHolders.middleName')}
                  error={errors.middleName}
                >
                  <input
                    id="middleName"
                    type="text"
                    placeholder={t('page.user.cis.certificateHolders.middleNamePlaceholder')}
                    value={state.middleName}
                    onChange={handleChangeNative('middleName', 'middleName')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="lastName"
                  label={t('page.user.cis.certificateHolders.lastName')}
                  required
                  error={errors.lastName}
                >
                  <input
                    id="lastName"
                    type="text"
                    placeholder={t('page.user.cis.certificateHolders.lastNamePlaceholder')}
                    value={state.lastName}
                    onChange={handleChangeNative('lastName', 'lastName')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="status"
                  label={t('page.user.cis.certificateHolders.status')}
                  value={statusOptions.find((option) => option.value === state.status) || ''}
                  clear={() => handleChangeSelect('status')(null)}
                  error={errors.status}
                >
                  <Select
                    id="status"
                    type="text"
                    placeholder={t('page.user.cis.certificateHolders.statusPlaceholder')}
                    value={statusOptions.find((option) => option.value === state.status) || ''}
                    options={statusOptions}
                    onChange={handleChangeSelect('status', 'status')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditCertificateHolder.propTypes = {
  state: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    title: PropTypes.string,
    middleName: PropTypes.string,
    birthDate: PropTypes.any,
    status: PropTypes.number,
  }),
  dispatch: PropTypes.func.isRequired,
  updateFieldCreator: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    title: PropTypes.string,
    middleName: PropTypes.string,
    birthDate: PropTypes.string,
    status: PropTypes.string,
  }),
  updateCertificateHolder: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
};

EditCertificateHolder.defaultProps = {
  errors: {},
};

export default EditCertificateHolder;
